import { inject } from "@angular/core";
import type { NiceAsyncTypeaheadSearchResult } from "@recursyve/ngx-material-components/typeahead";
import { NiceAsyncTypeaheadResourceProvider } from "@recursyve/ngx-material-components/typeahead";
import { isPropertyDefined } from "@recursyve/nice-ts-utils";
import type { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { ApiService } from "../../../../api/local-144/api.service";
import { findAllEmploymentSubSkills } from "../../../../api/local-144/fn/employment-sub-skills/find-all-employment-sub-skills";
import { getEmploymentSubSkillById } from "../../../../api/local-144/fn/employment-sub-skills/get-employment-sub-skill-by-id";
import type { EmploymentSubSkills } from "../../../../api/local-144/models/employment-sub-skills";

interface EmploymentSubSkillsTypeaheadSearchOptions {
    ignoreIds?: number[];
    employmentSkillId?: number;
}

export class EmploymentSubSkillsTypeaheadResource extends NiceAsyncTypeaheadResourceProvider<
    EmploymentSubSkills,
    number,
    EmploymentSubSkillsTypeaheadSearchOptions
> {
    private readonly api = inject(ApiService);

    public override readonly resource = "employment-sub-skills";

    private readonly pageSize = 10;

    public override getById(id: number): Observable<EmploymentSubSkills> {
        return this.api.invoke(getEmploymentSubSkillById, { id });
    }

    public override search(
        searchQuery: string,
        page: number,
        options: EmploymentSubSkillsTypeaheadSearchOptions = {}
    ): Observable<NiceAsyncTypeaheadSearchResult<EmploymentSubSkills>> {
        return this.api
            .invoke(findAllEmploymentSubSkills, {
                ignoreIds: options.ignoreIds,
                employmentSkillId: options.employmentSkillId,
                page,
                pageSize: this.pageSize,
                searchQuery
            })
            .pipe(
                map((items) => ({
                    items,
                    nextPage: items.length < this.pageSize ? page + 1 : null
                }))
            );
    }

    public override format(subSkill: EmploymentSubSkills): string {
        if (!isPropertyDefined(subSkill, "label")) {
            return "";
        }

        if (typeof subSkill.label !== "string") {
            return "";
        }

        return subSkill.label;
    }
}
